import { ContactUsValues } from '@/types/contact-us';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { userService } from '@/services';
import commonUtils from '@/utils/common';

export type ShowGlobalErrorFn = (args: {
  title?: string;
  message?: string;
  i18nKey?: string;
}) => void;

export type UseContactUsParams = {
  onCloseDrawer: () => void;
  /** Main app uses Redux `ui` modal; admin uses `error` slice — both render the shared GenericErrorModal. */
  showGlobalError?: ShowGlobalErrorFn;
};

const UseContactUs = ({
  onCloseDrawer,
  showGlobalError = commonUtils.showGlobalError,
}: UseContactUsParams) => {
  const { t } = useTranslation();
  const { user } = useAppSelector(authSelector);

  const [isLoading, setIsLoading] = useState(false);
  const [showMessageSent, setShowMessageSent] = useState(false);

  const InitialContactUsValues = {
    subject: '',
    email: '',
    phone: '',
    message: '',
    shouldReceiveUpdates: false,
  };

  const ContactUsSchema = Yup.object().shape({
    subject: Yup.string()
      .transform(value => (typeof value === 'string' ? value.trim() : ''))
      .required(t('fieldIsMandatory').toString()),
    email: Yup.string()
      .transform(value => (typeof value === 'string' ? commonUtils.sanitizeEmailInput(value) : ''))
      .required(t('fieldIsMandatory').toString())
      .test('valid-email', t('invalidEmail').toString(), value => commonUtils.isEmail(value ?? '')),
    phone: Yup.string().matches(/^[0-9]+$/, t('onlyNumbersAllowed').toString()),
    message: Yup.string()
      .transform(value => (typeof value === 'string' ? value.trim() : ''))
      .required(t('fieldIsMandatory').toString()),
    shouldReceiveUpdates: Yup.boolean(),
  });

  const OnContactUsSubmitted = async (values: ContactUsValues) => {
    setIsLoading(true);

    const { subject, email, phone, message, shouldReceiveUpdates } = values;

    try {
      const info = {
        userId: user?.metadata.id,
        subject,
        email,
        phone,
        description: typeof message === 'string' ? message.trim() : message,
        cfMarketingAgree: shouldReceiveUpdates,
      };

      const response = (await userService.sendContactInfo(user, info))?.data.data;

      if (response) {
        document.body.style.overflowY = 'hidden';
        window.scrollTo({
          top: 0,
        });
        setShowMessageSent(true);
      }

      setIsLoading(false);
    } catch (e) {
      console.log(e);
      showGlobalError({ i18nKey: 'error.somethingWentWrong' });
      setIsLoading(false);
    }
  };

  const onCloseMessageSent = () => {
    setShowMessageSent(false);
    onCloseDrawer();
    document.body.style.overflowY = '';
  };

  return {
    isLoading,
    InitialContactUsValues,
    ContactUsSchema,
    showMessageSent,
    onCloseMessageSent,
    OnContactUsSubmitted,
  };
};

export default UseContactUs;
